import { useState } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, Field } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';

export const TITLES: [string, string][] = [
  ['pastor', 'Pastor'], ['pastora', 'Pastora'], ['copastor', 'Co-pastor'], ['copastora', 'Co-pastora'], ['anciano', 'Anciano'], ['diacono', 'Diácono'], ['diaconisa', 'Diaconisa'],
  ['evangelista', 'Evangelista'], ['misionero', 'Misionero'], ['maestro', 'Maestro'], ['secretario', 'Secretario'], ['tesorero', 'Tesorero'], ['otro', 'Otro…'],
];
export type Appointment = { id: string; personId: string; name: string; title: string; customTitle: string | null; since: string | null };
export const titleLabel = (a: { title: string; customTitle: string | null }) => (a.title === 'otro' ? a.customTitle ?? 'Cargo' : TITLES.find(([k]) => k === a.title)?.[1] ?? a.title);

/** Nombramientos de una persona (Pastor, Anciano, Diácono…). Pastor(a) da además el rol de Pastor. */
export function AppointmentsCard({ churchId, personId, canEdit }: { churchId: string; personId: string; canEdit: boolean }) {
  const base = `/multichurch/churches/${churchId}/appointments`;
  const data = useAsync((signal) => api.get<{ items: Appointment[] }>(base, signal).then((r) => r.items.filter((a) => a.personId === personId)), [base, personId]);
  const [title, setTitle] = useState('pastor');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  if (data.status !== 'ready') return null;
  if (!canEdit && data.data.length === 0) return null;
  const run = async (action: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      setMsg({ tone: 'success', text: ok });
      data.reload();
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Nombramientos">
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {data.data.length === 0 ? <p className="muted small">Sin nombramientos.</p> : null}
      <ul className="list">
        {data.data.map((a) => (
          <li key={a.id} className="list__item">
            <span><strong>{titleLabel(a)}</strong>{a.since ? <span className="small muted"> · desde {new Date(`${a.since}T12:00:00`).toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' })}</span> : null}</span>
            {canEdit ? <Button variant="ghost" busy={busy} onClick={() => { if (window.confirm(`¿Terminar el nombramiento de ${titleLabel(a)}?`)) void run(() => api.delete(`${base}/${a.id}`), 'Nombramiento terminado.'); }}>Terminar</Button> : null}
          </li>
        ))}
      </ul>
      {canEdit ? (
        <form
          className="family-add"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            const since = String(f.get('since') ?? '') || null;
            const customTitle = String(f.get('customTitle') ?? '').trim() || null;
            void run(() => api.post(base, { personId, title, customTitle: title === 'otro' ? customTitle : null, since }), 'Nombramiento registrado.');
          }}
        >
          <div className="field">
            <label htmlFor="appt-title">Nombrar como</label>
            <select id="appt-title" value={title} onChange={(e) => setTitle(e.target.value)}>
              {TITLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          {title === 'otro' ? <Field label="Cargo" name="customTitle" required maxLength={60} /> : null}
          <Field label="Desde (opcional)" name="since" type="date" />
          <Button type="submit" busy={busy}>Nombrar</Button>
        </form>
      ) : null}
      {canEdit ? <p className="small muted">Pastor(a) y co-pastor(a) reciben el rol de Pastor: pueden administrar toda la iglesia, incluidos el plan y los pagos de KEORA.</p> : null}
    </Card>
  );
}
